import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { FileUpload } from '../../common/FileUpload';
import type { CourseLesson } from '../../../types/course';

interface LessonFormProps {
  onSubmit: (lessonData: Partial<CourseLesson>) => Promise<void>;
  onCancel: () => void;
  initialData?: CourseLesson;
}

export function LessonForm({ onSubmit, onCancel, initialData }: LessonFormProps) {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    type: initialData?.type || 'video',
    content: initialData?.content || '',
    duration: initialData?.duration || '',
    status: initialData?.status || 'draft',
    media: initialData?.media || [],
    attachments: initialData?.attachments || [],
    quiz: initialData?.quiz || { questions: [] }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Lesson title is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-white mb-2">Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="input w-full"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">Type</label>
        <select
          value={formData.type}
          onChange={e => setFormData(prev => ({ ...prev, type: e.target.value as CourseLesson['type'] }))}
          className="input w-full"
        >
          <option value="video">Video</option>
          <option value="reading">Reading</option>
          <option value="quiz">Quiz</option>
          <option value="assignment">Assignment</option>
        </select>
      </div>

      {formData.type === 'video' && (
        <div>
          <label className="block text-sm font-medium text-white mb-2">Video Upload</label>
          <FileUpload
            bucket="media"
            path="videos"
            onUpload={(url) => setFormData(prev => ({
              ...prev,
              media: [...prev.media, { url, type: 'video' }]
            }))}
            accept="video/*"
          />
        </div>
      )}

      {formData.type === 'reading' && (
        <div>
          <label className="block text-sm font-medium text-white mb-2">Content</label>
          <textarea
            value={formData.content}
            onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))}
            className="input w-full h-64"
            required
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-white mb-2">Duration</label>
        <input
          type="text"
          value={formData.duration}
          onChange={e => setFormData(prev => ({ ...prev, duration: e.target.value }))}
          className="input w-full"
          placeholder="e.g. 15 minutes"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">Status</label>
        <select
          value={formData.status}
          onChange={e => setFormData(prev => ({ ...prev, status: e.target.value as 'draft' | 'published' }))}
          className="input w-full"
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary"
        >
          {isSubmitting ? 'Saving...' : initialData ? 'Update Lesson' : 'Create Lesson'}
        </button>
      </div>
    </form>
  );
}